import { useState, useEffect } from 'react';
import { Container, Row, Col, Card, Button, ProgressBar, Spinner, Alert } from 'react-bootstrap';
import { useParams, useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import { FaKey } from 'react-icons/fa';
import { getEmployee, updateEmployee } from '../services/firebase/employeeService';
import { forgotPassword } from '../services/firebase/authService';
import Navbar from '../components/common/Navbar';
import Sidebar from '../components/common/Sidebar';
import PersonalDetailsForm from '../components/employee/PersonalDetailsForm';
import JobDetailsForm from '../components/employee/JobDetailsForm';
import BankDetailsForm from '../components/employee/BankDetailsForm';
import SalaryStructureForm from '../components/employee/SalaryStructureForm';
import { useAuth } from '../context/AuthContext';
import '../assets/css/employee.css';

const EditEmployeePage = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const [currentStep, setCurrentStep] = useState(1);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [resettingPassword, setResettingPassword] = useState(false);
  const [formData, setFormData] = useState(null);
  const [originalEmployee, setOriginalEmployee] = useState(null);
  const [errors, setErrors] = useState({});

  // Handle password reset
  const handleResetPassword = async () => {
    const email = formData?.personal?.email;
    if (!email) {
      toast.error('Employee email not found');
      return;
    }

    setResettingPassword(true);
    try {
      await forgotPassword(email);
      toast.success(`Password reset email sent to ${email}`);
    } catch (error) {
      console.error('Error sending password reset:', error);
      toast.error(error.message || 'Failed to send password reset email');
    } finally {
      setResettingPassword(false);
    }
  };

  const steps = [
    { number: 1, title: 'Personal Details', component: PersonalDetailsForm, key: 'personal' },
    { number: 2, title: 'Job Details', component: JobDetailsForm, key: 'job' },
    { number: 3, title: 'Bank Details', component: BankDetailsForm, key: 'bank' },
    { number: 4, title: 'Salary Structure', component: SalaryStructureForm, key: 'salary' }
  ];

  useEffect(() => {
    fetchEmployee();
  }, [id]);

  const fetchEmployee = async () => {
    setLoading(true);
    try {
      const response = await getEmployee(id);

      if (response.success) {
        const employee = response.data;
        setOriginalEmployee(employee);
        setFormData({
          personal: employee.personal || {},
          job: employee.job || {},
          bank: employee.bank || {},
          salary: employee.salary || {},
          careerJourney: employee.careerJourney || employee.workHistory || [],
          workHistory: employee.careerJourney || employee.workHistory || [],
          initialPassword: employee.initialPassword || '', // Load saved initial password
          isEditMode: true // Flag to show password as read-only
        });
      }
    } catch (error) {
      console.error('Error fetching employee:', error);
      toast.error('Failed to fetch employee details');
      navigate('/employees');
    } finally {
      setLoading(false);
    }
  };

  const handleInputChange = (section, field, value) => {
    setFormData(prev => ({
      ...prev,
      [section]: {
        ...prev[section],
        [field]: value
      }
    }));
  };

  const validateStep = (step) => {
    const newErrors = {};

    if (step === 1) {
      if (!formData.personal.firstName) newErrors.firstName = 'First name is required';
      if (!formData.personal.lastName) newErrors.lastName = 'Last name is required';
      if (!formData.personal.email) newErrors.email = 'Email is required';
      if (!formData.personal.phone) newErrors.phone = 'Phone is required';
    }

    if (step === 2) {
      if (!formData.job.department) newErrors.department = 'Department is required';
      if (!formData.job.designation) newErrors.designation = 'Designation is required';
      if (!formData.job.joiningDate) newErrors.joiningDate = 'Joining date is required';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleNext = () => {
    if (validateStep(currentStep)) {
      setCurrentStep(prev => Math.min(prev + 1, steps.length));
    }
  };

  const handlePrevious = () => {
    setCurrentStep(prev => Math.max(prev - 1, 1));
  };

  const handleSubmit = async () => {
    if (!validateStep(currentStep)) return;

    setSaving(true);
    try {
      const updatedPayload = { ...formData };

      // Check if critical career milestones (Salary, Designation, Department, Role) changed
      if (originalEmployee) {
        const prevSalary = parseFloat(originalEmployee.salary?.basicSalary) || 0;
        const newSalary = parseFloat(formData.salary?.basicSalary) || 0;
        const prevDesig = originalEmployee.job?.designation || '';
        const newDesig = formData.job?.designation || '';
        const prevDept = originalEmployee.job?.department || '';
        const newDept = formData.job?.department || '';
        const prevRole = originalEmployee.job?.role || originalEmployee.role || '';
        const newRole = formData.job?.role || '';

        const salaryChanged = newSalary > 0 && prevSalary > 0 && newSalary !== prevSalary;
        const roleOrDesigChanged = (newDesig && newDesig !== prevDesig) || (newDept && newDept !== prevDept) || (newRole && newRole !== prevRole);

        if (salaryChanged || roleOrDesigChanged) {
          const diff = newSalary - prevSalary;
          const pct = prevSalary > 0 ? ((diff / prevSalary) * 100) : 0;
          
          let milestoneType = 'promotion';
          let milestoneTitle = `Profile Updated: ${newDesig || prevDesig}`;

          if (salaryChanged && roleOrDesigChanged) {
            milestoneType = 'promotion_and_increment';
            milestoneTitle = `Promoted to ${newDesig} & Salary Revision`;
          } else if (salaryChanged) {
            milestoneType = 'salary_increment';
            milestoneTitle = `Salary Revision (${diff > 0 ? `+${pct.toFixed(1)}% Hike` : 'Adjusted'})`;
          } else if (roleOrDesigChanged) {
            milestoneType = 'promotion';
            milestoneTitle = `Designation updated to ${newDesig}`;
          }

          const autoMilestone = {
            id: `milestone_${Date.now()}_${Math.random().toString(36).substr(2, 6)}`,
            type: milestoneType,
            title: milestoneTitle,
            effectiveDate: new Date().toISOString().split('T')[0],
            previousDesignation: prevDesig,
            newDesignation: newDesig,
            previousDepartment: prevDept,
            newDepartment: newDept,
            previousRole: prevRole,
            newRole: newRole,
            previousSalary: prevSalary,
            newSalary: newSalary,
            incrementAmount: diff,
            incrementPercentage: parseFloat(pct.toFixed(2)),
            description: `Auto-recorded during profile update by ${user?.employeeName || user?.fullName || user?.email || 'Admin'}`,
            createdByName: user?.employeeName || user?.fullName || user?.email || 'Admin / HR',
            createdByUid: user?.uid || '',
            createdByRole: user?.role || 'admin',
            createdAt: new Date().toISOString()
          };

          const currentJourney = updatedPayload.careerJourney || [];
          updatedPayload.careerJourney = [autoMilestone, ...currentJourney];
          updatedPayload.workHistory = updatedPayload.careerJourney;
        }
      }

      const response = await updateEmployee(id, updatedPayload);

      if (response.success) {
        toast.success('Employee updated successfully');
        navigate(`/employees/${id}`);
      }
    } catch (error) {
      console.error('Error updating employee:', error);
      toast.error(error.message || 'Failed to update employee');
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="d-flex" style={{ width: '100%', overflowX: 'hidden' }}>
        <Sidebar />
        <div className="flex-grow-1 employee-main-content">
          <Navbar />
          <Container fluid className="py-3 py-md-4 px-2 px-md-4">
            <div className="text-center py-5">
              <Spinner animation="border" variant="primary" />
              <p className="mt-2">Loading employee data...</p>
            </div>
          </Container>
        </div>
      </div>
    );
  }

  if (!formData) {
    return null;
  }

  const CurrentStepComponent = steps[currentStep - 1].component;
  const currentStepKey = steps[currentStep - 1].key;
  const progress = (currentStep / steps.length) * 100;

  return (
    <div className="d-flex" style={{ width: '100%', overflowX: 'hidden' }}>
      <Sidebar />
      <div className="flex-grow-1 employee-main-content">
        <Navbar />
        <Container fluid className="py-3 py-md-4 px-2 px-md-4">
          <Row className="mb-3 mb-md-4 align-items-center g-2">
            <Col xs={12} sm={8}>
              <h2 className="fw-bold text-dark mb-1 fs-4 fs-md-2">Edit Employee</h2>
              <p className="text-muted mb-0 small">Update employee details</p>
            </Col>
            <Col xs={12} sm={4} className="text-start text-sm-end">
              <Button
                variant="outline-warning"
                size="sm"
                onClick={handleResetPassword}
                disabled={resettingPassword || !formData?.personal?.email}
                title="Send password reset email to employee"
                className="fw-bold"
              >
                <FaKey className="me-2" />
                {resettingPassword ? 'Sending...' : 'Reset Password'}
              </Button>
            </Col>
          </Row>
          
          {formData?.personal?.email && (
            <Alert variant="info" className="mb-3">
              <small>
                <strong>Login Credentials:</strong> Employee can login using email: <code>{formData.personal.email}</code>. 
                Use "Reset Password" button to send a password reset link.
              </small>
            </Alert>
          )}

          <Card className="shadow-sm border-0 rounded-4">
            <Card.Body className="p-3 p-md-4">
              {/* Progress Indicator */}
              <div className="mb-3 mb-md-4">
                <div className="d-flex justify-content-between mb-2">
                  {steps.map(step => (
                    <div
                      key={step.number}
                      className={`text-center ${currentStep >= step.number ? 'text-primary fw-bold' : 'text-muted'}`}
                      style={{ flex: 1 }}
                    >
                      <div
                        className={`rounded-circle d-inline-flex align-items-center justify-content-center mb-1 ${currentStep >= step.number ? 'bg-primary text-white shadow-sm' : 'bg-light'
                          }`}
                        style={{ width: '34px', height: '34px', fontSize: '0.85rem' }}
                      >
                        {step.number}
                      </div>
                      <div className="text-truncate px-1" style={{ fontSize: '0.72rem' }}>{step.title}</div>
                    </div>
                  ))}
                </div>
                <ProgressBar now={progress} variant="primary" style={{ height: '6px' }} />
              </div>

              {/* Form Content */}
              <CurrentStepComponent
                data={formData[currentStepKey]}
                onChange={handleInputChange}
                errors={errors}
                formData={formData}
              />

              {/* Navigation Buttons */}
              <div className="d-flex justify-content-between mt-4">
                <Button
                  variant="outline-secondary"
                  onClick={handlePrevious}
                  disabled={currentStep === 1}
                >
                  Previous
                </Button>

                <div>
                  <Button
                    variant="outline-secondary"
                    className="me-2"
                    onClick={() => navigate(`/employees/${id}`)}
                  >
                    Cancel
                  </Button>

                  {currentStep < 4 ? (
                    <Button variant="primary" onClick={handleNext}>
                      Next
                    </Button>
                  ) : (
                    <Button
                      variant="success"
                      onClick={handleSubmit}
                      disabled={saving}
                    >
                      {saving ? 'Updating...' : 'Update Employee'}
                    </Button>
                  )}
                </div>
              </div>
            </Card.Body>
          </Card>
        </Container>
      </div>
    </div>
  );
};

export default EditEmployeePage;
